import { FC, HTMLAttributes } from "react";
import Link from "next/link";
import clsx from "clsx";
import {
  ArrowRight,
  BarChart2,
  BookOpen,
  FileText,
  TrendingUp,
  Zap,
} from "react-feather";

import type { BlocksContent } from "@strapi/blocks-react-renderer";

// ----------------------------------------------------------------------------
// EducationMaterialSection — "Stay ahead" resource directory.
//
// v2 redesign: instead of the old 3-column illustration-heavy layout, this
// section is now a 4-card resource directory on a dark brand-blue band.
// Each card is a doorway to one content type (Insights, Case Studies,
// Industry Trends, Sample Reports). No competing CTA — the cards ARE the
// CTAs.
//
// Data flow: Strapi `list` field is a rich-text array. Each list item is
// authored as "some context text [Resource Name](url) more context text".
// `parseResourceItem` extracts the first link's text + URL as the card
// title + target, and the surrounding text as the card description.
// ----------------------------------------------------------------------------

export type IEducationMaterialSection = HTMLAttributes<HTMLElement> & {
  heading?: string;
  /** Raw Strapi rich-text list. We parse it ourselves so we can extract
   *  the embedded link target per item. */
  rawList?: { id: number; list_items: BlocksContent }[];
};

type Resource = {
  title: string;
  description: string;
  href: string;
  external?: boolean;
};

// Walks a single list_items rich-text array and pulls out the first link
// it finds. Returns {title, href, description} — title is the link's
// display text, description is the non-link text concatenated.
const parseResourceItem = (
  blocks: BlocksContent,
): Resource | null => {
  let title = "";
  let href = "";
  const descriptionParts: string[] = [];

  // Recursively walk children looking for a `link` node + accumulating
  // plain text. Strapi rich text is flat enough that one level is usually
  // sufficient, but recursion is cheap insurance against future authoring.
  type Node = {
    type?: string;
    text?: string;
    url?: string;
    children?: Node[];
  };
  const walk = (nodes: Node[] = []) => {
    nodes.forEach((node) => {
      if (node.type === "link" && !href) {
        href = node.url || "";
        title = (node.children || [])
          .map((c) => c.text || "")
          .join("")
          .trim();
      } else if (node.type === "text") {
        const t = (node.text || "").trim();
        if (t) descriptionParts.push(t);
      } else if (node.children) {
        walk(node.children);
      }
    });
  };

  blocks.forEach((block) => {
    if ("children" in block && Array.isArray(block.children)) {
      walk(block.children as Node[]);
    }
  });

  if (!title || !href) return null;
  return {
    title,
    href,
    description: descriptionParts
      .join(" ")
      .replace(/\s+/g, " ")
      .trim(),
    external: /^https?:\/\//.test(href),
  };
};

// Maps a resource URL to an icon. Pattern-matched so it survives editorial
// changes to the link's display text (e.g. "Read our sample reports" still
// gets the report icon as long as the URL contains "sample-report").
const iconFor = (href: string) => {
  if (/sample-report/.test(href)) return BarChart2;
  if (/case-stud/.test(href)) return FileText;
  if (/leadership|insight/.test(href)) return Zap;
  if (/trend|industry/.test(href)) return TrendingUp;
  return BookOpen;
};

const EducationMaterialSection: FC<IEducationMaterialSection> = (props) => {
  const { heading, rawList, className, ...other } = props;

  const resources = (rawList ?? [])
    .map((item) => parseResourceItem(item.list_items))
    .filter((r): r is Resource => r !== null);

  if (!heading && resources.length === 0) return null;

  return (
    <section
      className={clsx(
        // Full-bleed dark brand band — same primary-900 family the
        // schedule-meet band uses, so the two dark bands on the page
        // visually rhyme. Compact vertical rhythm so this section fits
        // in a single viewport on desktop instead of pushing the next
        // section below the fold.
        "relative isolate w-full overflow-hidden bg-primary-900 py-8 md:py-10 lg:py-12",
        className,
      )}
      {...other}
    >
      {/* Soft secondary-blue glow in the corners — depth without
          decoration. Pure transform → cheap to render. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-secondary-900/30 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -left-32 h-80 w-80 rounded-full bg-secondary-900/20 blur-3xl"
      />

      <div className="container relative mx-auto">
        {/* Header — inline since SectionHeader doesn't have a brand-on-brand
            dark variant for primary-900 backgrounds. Tightened spacing
            (mb-6/mb-8 instead of mb-10/12/14) so the cards sit closer to
            the heading and the whole section reads as one tight unit. */}
        <div className="mx-auto mb-6 max-w-2xl text-center md:mb-8">
          <p className="mb-2 font-bricolage-grotesque text-[clamp(0.75rem,0.4vw+0.65rem,0.875rem)] font-semibold uppercase tracking-[0.25em] text-yellow-400">
            Resources
          </p>
          {heading && (
            <h2 className="font-bricolage-grotesque text-[clamp(1.25rem,2vw+0.75rem,2.25rem)] font-medium !leading-[1.15] text-white">
              {heading}
            </h2>
          )}
        </div>

        {/* 4-card resource grid */}
        {resources.length > 0 && (
          <ul
            className={clsx(
              "mx-auto grid list-none gap-4 sm:gap-5 lg:gap-6",
              // Layout scales: 1 → 2 → 4 columns, never more than 4.
              resources.length === 1
                ? "max-w-sm grid-cols-1"
                : resources.length === 2
                  ? "max-w-2xl grid-cols-1 sm:grid-cols-2"
                  : resources.length === 3
                    ? "max-w-4xl grid-cols-1 sm:grid-cols-3"
                    : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
            )}
          >
            {resources.map((resource, idx) => {
              const Icon = iconFor(resource.href);
              return (
                <li key={idx}>
                  <Link
                    href={resource.href}
                    target={resource.external ? "_blank" : undefined}
                    rel={
                      resource.external ? "noopener noreferrer" : undefined
                    }
                    className="group flex h-full flex-col gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.07] md:gap-4 md:p-5"
                  >
                    {/* Icon + arrow on the same row — saves vertical space
                        so each card stays compact. */}
                    <div className="flex items-center justify-between">
                      <span
                        aria-hidden="true"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-400/15 text-yellow-400 ring-1 ring-yellow-400/30 transition-colors duration-300 group-hover:bg-yellow-400/25"
                      >
                        <Icon size={18} strokeWidth={2} />
                      </span>
                      <ArrowRight
                        size={16}
                        className="text-yellow-400 transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </div>

                    {/* Title — slightly tighter scale than the previous
                        16-22px so 4 cards fit nicely on tablet. */}
                    <h3 className="font-bricolage-grotesque text-[clamp(1rem,0.5vw+0.875rem,1.125rem)] font-medium !leading-tight text-white">
                      {resource.title}
                    </h3>

                    {/* Description — commented out per user request to
                        keep cards minimal (title + icon + arrow only).
                        Restore the block below if a future iteration
                        wants the descriptor back. */}
                    {/*
                    {resource.description && (
                      <p className="line-clamp-2 text-[clamp(0.75rem,0.2vw+0.7rem,0.875rem)] font-light leading-snug text-white/65 lg:font-extralight">
                        {resource.description}
                      </p>
                    )}
                    */}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
};

export default EducationMaterialSection;
